import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PinnedItemType, usePinnedItems } from "services/pinnedItems";
import { useUsers } from "services/users/useUsers";
import { User } from "services/users/user.types";

import UserIcon24 from "assets/icons/UserIcon24.svg?react";

import { useMenu } from "components/ActionMenu/useMenu";
import Highlighter from "components/Highligher/Highlighter";
import { PinIconEmpty, PinIconFill } from "components/Icons";
import { Menu } from "components/LeftSidebar/RecordMenu/RecordMenu";
import { useUserMenuItems } from "components/LeftSidebar/SecurityPanel/UserRecord";
import DirectorySearchPanel from "components/LeftSidebarV2/DirectorySearchPanel/DirectorySearchPanel";

import styles from "./UsersDirectory.module.scss";

const UserItem = (props: {
  user: User;
  isPinned: boolean;
  onTogglePin: () => void;
  searchQuery: string;
}) => {
  const { user, isPinned, onTogglePin, searchQuery } = props;
  const menu = useMenu();

  const { items, modalsMarkup } = useUserMenuItems({
    user,
    menu,
  });
  return (
    <>
      <div className={styles.userItem}>
        <div className={styles.left}>
          <div className={styles.icon}>
            <UserIcon24 />
          </div>
          <div className={styles.title}>
            {searchQuery ? (
              <Highlighter
                searchWords={[searchQuery]}
                textToHighlight={user.userName}
              />
            ) : (
              user.userName
            )}
          </div>
        </div>
        <div className={styles.right}>
          <div
            className={styles.pinButton}
            onClick={onTogglePin}
          >
            {isPinned ? <PinIconFill /> : <PinIconEmpty />}
          </div>
          <Menu
            testId="user-more-icon"
            items={items}
            menu={menu}
          />
        </div>
      </div>
      {modalsMarkup}
    </>
  );
};

const UsersDirectory = () => {
  const { t } = useTranslation();
  const users = useUsers();
  const { pinnedItems, togglePinned } = usePinnedItems(PinnedItemType.USER);
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [search]);

  const filteredUsers = useMemo(() => {
    return users.filter(user =>
      user.userName.toLowerCase().includes(search.toLowerCase())
    );
  }, [users, search]);

  const sortedUsers = useMemo(() => {
    const pinnedSet = new Set(pinnedItems);
    const pinned = filteredUsers.filter(user => pinnedSet.has(user.userId));
    const unpinned = filteredUsers.filter(user => !pinnedSet.has(user.userId));

    return { pinned, unpinned };
  }, [filteredUsers, pinnedItems]);

  return (
    <div className={styles.usersDirectory}>
      <div className={styles.searchPanel}>
        <DirectorySearchPanel
          onSearch={setSearch}
          search={search}
          testId="users-search-input"
        />
      </div>

      <div
        className={styles.userList}
        ref={listRef}
      >
        {sortedUsers.pinned.length > 0 && (
          <div className={styles.pinnedUsersHeader}>
            {t("workspace.directory.pinned")}
          </div>
        )}
        {sortedUsers.pinned.map(user => (
          <UserItem
            user={user}
            key={user.userId}
            isPinned={true}
            onTogglePin={() => togglePinned(user.userId)}
            searchQuery={search}
          />
        ))}
        {sortedUsers.pinned.length > 0 && sortedUsers.unpinned.length > 0 && (
          <div className={styles.separator} />
        )}
        {sortedUsers.unpinned.map(user => (
          <UserItem
            user={user}
            key={user.userId}
            isPinned={false}
            onTogglePin={() => togglePinned(user.userId)}
            searchQuery={search}
          />
        ))}
      </div>
    </div>
  );
};

export default UsersDirectory;
